/**
 * @file index.tsx
 *
 * @fileoverview An alert component that focus on accessibility.
 */

import { CloseIcon } from "@jq-icons/Close";
import { ReactNode, useEffect, useRef } from "react";
import styles from "./alert.module.scss";
import { cva, VariantProps } from "class-variance-authority";
import { motion } from "framer-motion";

const alert = cva([styles.alert], {
  variants: {
    alertStyle: {
      success: styles.alertSuccess,
      warning: styles.alertWarning,
      danger: styles.alertDanger,
    },
  },
  defaultVariants: {
    alertStyle: "success",
  },
});
const alertButton = cva([styles.alertButton], {
  variants: {
    alertStyle: {
      success: styles.alertButtonSuccess,
      warning: styles.alertButtonWarning,
      danger: styles.alertButtonDanger,
    },
  },
  defaultVariants: {
    alertStyle: "success",
  },
});

export interface MessageAlertProps extends Partial<VariantProps<typeof alert>> {
  /**
   * The contents on the alert message.
   */
  children: ReactNode | string;
  /**
   * Flag to activate automatic scrolling to message on appearance.
   *
   * @default false
   */
  scrollToMessage?: boolean;
  /**
   * Accessibility information for the screen readers.
   *
   * @default "polite"
   */
  type?: "assertive" | "polite";
  /**
   * Event to call when closing the message.
   */
  onClose?: () => void;
}

/**
 * The message alert is a component that can be rendered on the page and when rendered will warn the screen reader that something happened. It has different levels
 * of severity according to ARIA specifications. It should be used to prompt the user with information at real time. It uses aria live attributes to display this information
 * to screen readers acccordingly. Refer to [The aria documentation](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions) for more information
 * on accessible alerts.
 */
export const MessageAlert = ({
  scrollToMessage = false,
  children,
  onClose,
  alertStyle = "success",
  type = "polite",
}: MessageAlertProps) => {
  const messageRef = useRef(null) as any;

  // Automatically scroll to MessageAlert element if scrollToMessage is set
  useEffect(() => {
    if (messageRef.current && scrollToMessage) {
      messageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, []);

  return (
    <motion.div
      initial={{
        opacity: 0,
      }}
      animate={{
        opacity: 1,
      }}
      exit={{
        opacity: 0,
      }}
      transition={{ duration: 0.25, ease: "anticipate" }}
    >
      <div
        className={alert({ alertStyle })}
        role="status"
        aria-live={type}
        data-test-id="jcl-alert"
      >
        <div ref={messageRef} />
        {children}
        {onClose && (
          <button
            onClick={onClose}
            data-testid="close-button"
            className={alertButton({ alertStyle })}
          >
            <CloseIcon height="20" width="20" title="Close" />
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default MessageAlert;
